import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { announceReactionReply } from "../utils/reaction_reply"

// The icon row under one of Jane's replies: read aloud, thumbs up/down, redo.
// (Copy is the shared copy-text button; reactions are the emoji picker beside
// it.) Everything here is a plain fetch with no re-render of its own - feedback
// just flips the pressed state, redo hands off to the chat poller through the
// same "reply started" signal a reaction uses.
export default class extends Controller {
    static targets = ["speak", "up", "down", "redo"]
    static values = { turnId: Number, feedbackUrl: String, redoUrl: String }

    speak(event) {
        event.preventDefault()
        if (this._audio && !this._audio.paused) {
            this._audio.pause()
            this._setPlaying(false)
            return
        }

        this._audio = new Audio(`/assistant/speak?turn_id=${encodeURIComponent(this.turnIdValue)}`)
        this._audio.addEventListener("ended", () => this._setPlaying(false))
        this._audio.addEventListener("error", () => this._setPlaying(false))
        this._setPlaying(true)
        this._audio.play().catch(() => this._setPlaying(false))
    }

    up(event) { this._rate(event, "up") }
    down(event) { this._rate(event, "down") }

    async redo(event) {
        event.preventDefault()
        if (!this.hasRedoTarget || this.redoTarget.disabled) return

        this.redoTarget.disabled = true
        try {
            const response = await fetch(this.redoUrlValue, { method: "POST", headers: this._headers() })
            if (response.ok) announceReactionReply(response)
            else this.redoTarget.disabled = false
        } catch {
            this.redoTarget.disabled = false
        }
    }

    async _rate(event, value) {
        event.preventDefault()
        const button = event.currentTarget
        const next = button.getAttribute("aria-pressed") === "true" ? "" : value
        this._press(next)
        const body = new FormData()
        body.append("value", next)
        try {
            const response = await fetch(this.feedbackUrlValue, { method: "POST", headers: this._headers(), body })
            if (!response.ok) this._press(next ? "" : value)
        } catch {
            this._press(next ? "" : value)
        }
    }

    _press(value) {
        if (this.hasUpTarget) this._mark(this.upTarget, value === "up", "bi-hand-thumbs-up")
        if (this.hasDownTarget) this._mark(this.downTarget, value === "down", "bi-hand-thumbs-down")
    }

    _mark(button, pressed, base) {
        button.setAttribute("aria-pressed", pressed ? "true" : "false")
        const icon = button.querySelector("i")
        if (icon) icon.className = `bi ${pressed ? `${base}-fill` : base}`
    }

    _setPlaying(playing) {
        if (!this.hasSpeakTarget) return
        this.speakTarget.setAttribute("aria-pressed", playing ? "true" : "false")
        const icon = this.speakTarget.querySelector("i")
        if (icon) icon.className = `bi ${playing ? "bi-stop-circle" : "bi-volume-up"}`
    }

    _headers() {
        return { "X-CSRF-Token": csrfToken(), Accept: "text/plain" }
    }
}
